import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import {
  ExternalLink,
  Disc3,
  Mic,
  Film,
  Building2,
  Award,
  Newspaper,
  Camera,
  HelpCircle,
  BadgeCheck,
  Fingerprint,
  CalendarDays,
  Cpu,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  X,
  Play,
} from "lucide-react";
import { api, formatApiError, imgUrl } from "@/lib/api";
import { toast } from "sonner";
import SectionLabel from "@/components/SectionLabel";
import { MediaStrip } from "@/components/MediaStrip";
import { SITE_ORIGIN } from "@/lib/seo";
import { HERO_NINA } from "@/lib/assets";
import {
  NAME,
  LEGAL_NAME,
  ROLES,
  BIOS,
  EXPERTISE,
  LINKEDIN_EXPERIENCE_URL,
  TIMELINE,
  ALBUMS,
  FILMOGRAPHY,
  PODCASTS,
  BUSINESSES,
  SPEAKING_TOPICS,
  PROFILES,
  ENTITY_IDS,
  AUTHORITY_SOURCES,
  FAQS,
  SINGLES as SINGLES_FALLBACK,
} from "@/lib/aboutContent";

const NAV = [
  ["Biography", "biography"],
  ["Expertise", "expertise"],
  ["Timeline", "timeline"],
  ["Heritage", "heritage"],
  ["Dedication", "dedication"],
  ["Discography", "discography"],
  ["Filmography", "filmography"],
  ["Podcasts", "podcasts"],
  ["Businesses", "businesses"],
  ["Awards", "awards"],
  ["Media", "media"],
  ["Gallery", "gallery"],
  ["FAQs", "faqs"],
  ["Profiles", "profiles"],
  ["Sources", "authority"],
  ["Booking", "booking"],
];

const SOUND_CHEQUE_PHOTOS = [
  { src: "/api/static/loni/soundcheque-1.jpg", alt: "Loni Gamble & Sound Cheque band portrait" },
  { src: "/api/static/loni/soundcheque-2.jpg", alt: "Loni Gamble & Sound Cheque performing" },
  { src: "/api/static/loni/soundcheque-3.jpg", alt: "Loni Gamble & Sound Cheque group photo" },
  { src: "/api/static/loni/soundcheque-rebirth.jpg", alt: "Loni Gamble & Sound Cheque — The Rebirth" },
];

const ERAS = [
  { key: "come-up", label: "The Come-Up", range: "1991 \u2013 1995", min: 1991, max: 1995 },
  { key: "phat-farm", label: "Phat Farm & The Label", range: "2001 \u2013 2005", min: 2001, max: 2005 },
  { key: "ventures", label: "Ventures & Advocacy", range: "2010 \u2013 2024", min: 2010, max: 2024 },
  { key: "now", label: "Two Albums, One Era", range: "2026", min: 2026, max: 2999 },
];

function EraTimeline() {
  const [open, setOpen] = useState("come-up");
  const grouped = ERAS.map((era) => ({
    ...era,
    items: TIMELINE.filter((t) => {
      const y = parseInt(String(t.date).match(/\d{4}/)?.[0] || "0", 10);
      return y >= era.min && y <= era.max;
    }),
  })).filter((e) => e.items.length);

  return (
    <div className="space-y-3" data-testid="career-eras">
      {grouped.map((era) => {
        const isOpen = open === era.key;
        return (
          <div key={era.key} className="surface overflow-hidden">
            <button
              type="button"
              onClick={() => setOpen(isOpen ? null : era.key)}
              aria-expanded={isOpen}
              className="w-full flex items-center justify-between gap-4 px-5 sm:px-7 py-5 text-left hover:bg-white/[0.02] transition-colors"
              data-testid={`era-toggle-${era.key}`}
            >
              <span className="flex items-baseline gap-3 flex-wrap">
                <span className="font-serif text-xl sm:text-2xl tracking-tight" style={{ color: "var(--gold)" }}>
                  {era.label}
                </span>
                <span className="text-[11px] uppercase tracking-[0.2em] text-white/40">{era.range}</span>
              </span>
              <ChevronDown
                size={20}
                className="shrink-0 text-white/50 transition-transform duration-300"
                style={{ transform: isOpen ? "rotate(180deg)" : "none" }}
              />
            </button>
            {isOpen && (
              <div className="px-5 sm:px-7 pb-7" data-testid={`era-panel-${era.key}`}>
                <ol className="relative border-l border-white/10 pl-8 space-y-8">
                  {era.items.map((t, i) => (
                    <li key={i} className="relative" data-testid={`timeline-${era.key}-${i}`}>
                      <span
                        className="absolute -left-[38px] top-1 w-3 h-3 rounded-full border-2"
                        style={{ borderColor: "var(--gold)", background: "#050308" }}
                      />
                      <div className="flex items-baseline gap-3 flex-wrap">
                        <span className="font-serif text-lg tracking-tight text-white">{t.phase}</span>
                        <span className="text-[11px] uppercase tracking-[0.2em] text-white/40">{t.date}</span>
                      </div>
                      <p className="text-white/65 mt-2 max-w-2xl leading-relaxed text-sm sm:text-base">{t.text}</p>
                    </li>
                  ))}
                </ol>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function Section({ id, icon: Icon, number, label, title, children }) {
  return (
    <section id={id} className="scroll-mt-28 py-14 border-t border-white/5" data-testid={`about-${id}`}>
      <div className="flex items-center gap-3 mb-8">
        {Icon && <Icon size={16} style={{ color: "var(--gold)" }} strokeWidth={1.6} />}
        <SectionLabel number={number} label={label} />
      </div>
      {title && (
        <h2 className="font-serif font-light tracking-tighter text-3xl sm:text-4xl lg:text-5xl leading-[0.95] mb-8">
          {title}
        </h2>
      )}
      {children}
    </section>
  );
}

const BIO_TABS = [
  ["Short", "short"],
  ["Medium", "medium"],
  ["Long", "long"],
];

const GALLERY_PHOTOS = [
  { src: HERO_NINA, alt: "Nina Capone", cat: "Portraits" },
  { src: imgUrl("/api/static/gallery/nina-portrait.jpg"), alt: "Nina Capone \u2014 portrait", cat: "Portraits" },
  { src: imgUrl("/api/static/gallery/nicetown-live.jpg"), alt: "Live at the Nicetown \u2018Give Back to the Community\u2019 Festival", cat: "Live" },
  { src: imgUrl("/api/static/gallery/live-toke-jones.jpg"), alt: "Live performance alongside Toke Jones", cat: "Live" },
  { src: imgUrl("/api/static/gallery/beanie-sigel.jpg"), alt: "With Roc The Barber & Beanie Sigel", cat: "With Artists" },
  { src: imgUrl("/api/static/gallery/eness-mathis.jpg"), alt: "With Bad Boy\u2019s E.N.E.S.S. (Eness Mathis)", cat: "With Artists" },
  { src: imgUrl("/api/static/gallery/bariq-cobbs.jpg"), alt: "With Bariq Cobbs of Miskeen Originals", cat: "With Artists" },
  { src: imgUrl("/api/static/gallery/tech-panel.jpg"), alt: "Technology panel with AI & fashion consultant Charles Postell", cat: "Panels & Press" },
  { src: imgUrl("/api/static/gallery/raymond-anthony.jpg"), alt: "With comedy promoter Raymond Anthony", cat: "Community" },
  { src: imgUrl("/api/static/gallery/community-festival.jpg"), alt: "Out in the community", cat: "Community" },
  { src: ALBUMS[0].image, alt: `${ALBUMS[0].title} album cover`, cat: "Covers" },
  { src: ALBUMS[1].image, alt: `${ALBUMS[1].title} album cover`, cat: "Covers" },
];


export default function About() {
  const [bioLen, setBioLen] = useState("medium");
  const [press, setPress] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    organization: "",
    booking_type: "music",
    event_date: "",
    location: "",
    budget: "",
    message: "",
  });
  const [singles, setSingles] = useState(SINGLES_FALLBACK);
  const [dbPhotos, setDbPhotos] = useState([]);
  const [galleryCat, setGalleryCat] = useState("All");
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const touchStartX = useRef(null);

  const allPhotos = [
    ...GALLERY_PHOTOS,
    ...dbPhotos.map((d) => ({
      src: imgUrl(d.image),
      alt: d.caption || "Gallery photo",
      cat: d.category || "Gallery",
    })),
  ];
  const galleryCats = ["All", ...Array.from(new Set(allPhotos.map((p) => p.cat)))];
  const galleryFiltered =
    galleryCat === "All" ? allPhotos : allPhotos.filter((p) => p.cat === galleryCat);
  const lightboxPhoto = lightboxIdx != null ? galleryFiltered[lightboxIdx] : null;
  const showPrev = () => setLightboxIdx((i) => (i - 1 + galleryFiltered.length) % galleryFiltered.length);
  const showNext = () => setLightboxIdx((i) => (i + 1) % galleryFiltered.length);
  const onTouchStart = (e) => { touchStartX.current = e.changedTouches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null || galleryFiltered.length <= 1) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (dx > 45) showPrev();
    else if (dx < -45) showNext();
    touchStartX.current = null;
  };

  useEffect(() => {
    if (lightboxIdx == null) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setLightboxIdx(null);
      else if (e.key === "ArrowLeft") showPrev();
      else if (e.key === "ArrowRight") showNext();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lightboxIdx, galleryFiltered.length]);

  useEffect(() => {
    api.get("/public/gallery").then((r) => setDbPhotos(r.data.items || [])).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/public/press").then((r) => setPress(r.data.items || [])).catch(() => {});
    api
      .get("/public/singles")
      .then((r) => {
        const list = r.data.singles || [];
        if (list.length) setSingles(list);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const graph = [
      {
        "@type": "ProfilePage",
        "@id": `${SITE_ORIGIN}/about#profilepage`,
        url: `${SITE_ORIGIN}/about`,
        name: "About Nina Capone — Official Biography, Discography & Press",
        dateModified: new Date().toISOString(),
        isPartOf: { "@id": `${SITE_ORIGIN}/#website` },
        about: { "@id": `${SITE_ORIGIN}/#nina` },
        mainEntity: { "@id": `${SITE_ORIGIN}/#nina` },
        speakable: {
          "@type": "SpeakableSpecification",
          cssSelector: ["h1", "[data-testid=about-intro]"],
        },
      },
      {
        "@type": "FAQPage",
        "@id": `${SITE_ORIGIN}/about#faq`,
        mainEntity: Object.values(FAQS)
          .flat()
          .map((item) => ({
            "@type": "Question",
            name: item.q,
            acceptedAnswer: { "@type": "Answer", text: item.a },
          })),
      },
    ];
    const data = { "@context": "https://schema.org", "@graph": graph };
    let el = document.getElementById("about-jsonld");
    if (!el) {
      el = document.createElement("script");
      el.type = "application/ld+json";
      el.id = "about-jsonld";
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(data);
    return () => {
      const node = document.getElementById("about-jsonld");
      if (node) node.remove();
    };
  }, []);

  const submitBooking = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/public/booking", form);
      toast.success("Request sent — Nina's team will be in touch shortly.");
      setForm({ ...form, name: "", email: "", phone: "", organization: "", event_date: "", location: "", budget: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const input =
    "w-full bg-white/[0.03] border border-white/10 rounded-lg px-4 py-3 text-sm text-white placeholder-white/30 focus:border-[color:var(--gold)]/60 focus:outline-none transition-colors";

  return (
    <div className="bg-[#050308] text-white" data-testid="about-page">
      {/* HERO */}
      <section className="relative overflow-hidden pt-24 pb-20" data-testid="about-hero">
        <div
          className="absolute inset-0 opacity-25"
          style={{ backgroundImage: `url(${HERO_NINA})`, backgroundSize: "cover", backgroundPosition: "center top" }}
        />
        <div
          className="absolute inset-0"
          style={{ background: "linear-gradient(180deg, rgba(5,3,8,0.75) 0%, rgba(5,3,8,0.9) 60%, #050308 100%)" }}
        />
        <div className="relative max-w-[1200px] mx-auto px-6 lg:px-12">
          <SectionLabel number="The Reference" label="About / Knowledge Hub" />
          <h1 className="font-serif font-light tracking-tighter text-[3.2rem] sm:text-7xl lg:text-8xl leading-[0.85] mt-4">
            Nina <em className="italic" style={{ color: "var(--gold)" }}>Capone.</em>
          </h1>
          <p className="mt-4 text-sm uppercase tracking-[0.28em] text-white/50">
            Legal name · {LEGAL_NAME}
          </p>
          <div className="mt-7 flex flex-wrap gap-2.5" data-testid="about-roles">
            {ROLES.map((r) => (
              <span
                key={r}
                className="px-4 py-1.5 rounded-full text-[11px] uppercase tracking-[0.2em] border"
                style={{ borderColor: "rgba(232,180,184,0.35)", color: "var(--gold)", background: "rgba(232,180,184,0.06)" }}
              >
                {r}
              </span>
            ))}
          </div>
        </div>
      </section>

      <MediaStrip />

      <div className="max-w-[1200px] mx-auto px-6 lg:px-12 flex gap-12">
        {/* Sticky TOC */}
        <aside className="hidden xl:block w-52 shrink-0">
          <nav className="sticky top-28 space-y-1.5" data-testid="about-toc">
            {NAV.map(([label, id]) => (
              <a
                key={id}
                href={`#${id}`}
                className="block text-xs uppercase tracking-[0.18em] text-white/45 hover:text-[color:var(--gold)] transition-colors py-1"
              >
                {label}
              </a>
            ))}
          </nav>
        </aside>

        <div className="flex-1 min-w-0">
          {/* BIOGRAPHY */}
          <Section id="biography" icon={BadgeCheck} number="01" label="Biography">
            <div className="font-serif text-xl sm:text-2xl text-white/85 leading-[1.5] max-w-3xl mb-8 space-y-4" data-testid="about-intro">
              <p>
                <span className="text-white">
                  Nina Capone
                  <sup className="align-super text-[0.5em] tracking-normal opacity-80">™</sup>
                </span>{" "}
                is the professional stage name of Jamilah N. Lawry, a
                Philadelphia recording artist, media personality, entrepreneur,
                radio executive, and community advocate.
              </p>
              <p>
                Through her music, live performances, media platforms, and
                business ventures, Nina Capone has built a multifaceted
                entertainment brand rooted in independence, authority,
                creativity, and service to the community.
              </p>
            </div>
            <div className="flex gap-2 mb-6" data-testid="bio-tabs">
              {BIO_TABS.map(([label, key]) => (
                <button
                  key={key}
                  onClick={() => setBioLen(key)}
                  data-testid={`bio-tab-${key}`}
                  className={`px-4 py-2 rounded-full text-xs uppercase tracking-[0.2em] border transition-all ${
                    bioLen === key
                      ? "border-[color:var(--gold)]/60 text-[color:var(--gold)] bg-[color:var(--gold)]/5"
                      : "border-white/10 text-white/50 hover:text-white/80"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            <div className="font-serif text-lg sm:text-xl text-white/80 leading-[1.6] space-y-5 max-w-3xl" data-testid="bio-body">
              {bioLen === "long" ? (
                BIOS.long.map((p, i) => <p key={i}>{p}</p>)
              ) : (
                <p>{BIOS[bioLen]}</p>
              )}
            </div>
          </Section>

          {/* EXPERTISE — Technology & AI */}
          <Section id="expertise" icon={Cpu} label="Technology & AI · Expertise" title="Built by a technologist.">
            <p className="text-white/70 max-w-3xl leading-relaxed mb-7 text-base sm:text-lg">
              Beyond the mic, Jamilah is a hands-on technologist and IT expert. She built the
              digital, AI, and IT backbone of her ventures herself — platforms, automation, and
              media systems — and brings that real, built-it-myself experience to clients through{" "}
              <span style={{ color: "var(--gold)" }}>My Biz Consulting</span>.
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-7">
              {EXPERTISE.map((e) => (
                <div
                  key={e}
                  className="surface px-4 py-3.5 flex items-center gap-3 text-sm text-white/80"
                  data-testid="expertise-item"
                >
                  <Cpu size={15} style={{ color: "var(--gold)" }} strokeWidth={1.7} />
                  {e}
                </div>
              ))}
            </div>
            <a
              href={LINKEDIN_EXPERIENCE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="link-gold inline-flex items-center gap-2 text-sm"
              data-testid="expertise-linkedin"
            >
              <BadgeCheck size={15} /> Verify professional experience on LinkedIn →
            </a>
          </Section>

          {/* TIMELINE */}
          <Section id="timeline" icon={CalendarDays} number="02" label="Career Timeline" title="The arc so far.">
            <p className="text-white/55 text-sm mb-6">Tap an era to expand.</p>
            <EraTimeline />
          </Section>

          {/* HERITAGE — PARENTS */}
          <Section id="heritage" icon={Disc3} number="02b" label="Heritage" title="The music that made her.">
            <p className="text-white/60 leading-relaxed max-w-2xl mb-8 text-sm sm:text-base">
              Three names. Three sounds. The Philadelphia music inheritance that
              runs through every Nina Capone record &mdash; passed down from stage to
              studio to home.
            </p>
            <div className="grid md:grid-cols-3 gap-6">
              {/* Mother */}
              <article className="surface p-8 lg:p-10 space-y-5" data-testid="heritage-mother">
                <img
                  src={imgUrl("/api/static/heritage/spice.jpg")}
                  alt="LaVern &ldquo;Spice&rdquo; Cohen performing"
                  loading="lazy"
                  className="w-24 h-24 rounded-full object-cover object-top border border-[color:var(--gold)]/30"
                  data-testid="heritage-mother-photo"
                />
                <p className="eyebrow">Mother</p>
                <h3 className="font-serif text-3xl lg:text-4xl tracking-tighter font-light leading-tight">
                  LaVern &ldquo;Spice&rdquo;
                </h3>
                <p className="font-serif italic text-base text-white/60">Vocalist &middot; Sound Cheque Band</p>
                <p className="text-sm text-white/60 leading-relaxed">
                  One half of the celebrated <em className="italic">Sugar &amp; Spice</em> vocal
                  duo, Nina&rsquo;s mother toured the country with Loni Gamble&rsquo;s Sound Cheque
                  band &mdash; bringing Motown jams and Philly soul to stages from coast to
                  coast. The first voice Nina ever modeled.
                </p>
                <a
                  href="https://www.youtube.com/results?search_query=Loni+Gamble+%26+Sound+Cheque+Sugar+%26+Spice"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em]"
                  style={{ color: "var(--gold)" }}
                  data-testid="heritage-mother-link"
                >
                  Listen On YouTube
                </a>
              </article>

              {/* Father */}
              <article className="surface p-8 lg:p-10 space-y-5" data-testid="heritage-father">
                <img
                  src={imgUrl("/api/static/heritage/yakub.jpg")}
                  alt="Thomas &ldquo;Yakub&rdquo; at the DJ setup"
                  loading="lazy"
                  className="w-24 h-24 rounded-full object-cover object-top border border-[color:var(--gold)]/30"
                  data-testid="heritage-father-photo"
                />
                <p className="eyebrow">Father</p>
                <h3 className="font-serif text-3xl lg:text-4xl tracking-tighter font-light leading-tight">
                  Thomas &ldquo;Yakub&rdquo;
                </h3>
                <p className="font-serif italic text-base text-white/60">DJ &middot; Promoter</p>
                <p className="text-sm text-white/60 leading-relaxed">
                  A working DJ and promoter who taught Nina the room before the
                  record &mdash; how a crowd breathes, how a bass line lifts the floor, how
                  an event is a story. The reason she built a station instead of
                  waiting for one to play her.
                </p>
              </article>

              {/* Step-Father */}
              <article className="surface p-8 lg:p-10 space-y-5" data-testid="heritage-stepfather">
                <img
                  src={imgUrl("/api/static/heritage/ted.jpg")}
                  alt="Ted Cohen playing guitar"
                  loading="lazy"
                  className="w-24 h-24 rounded-full object-cover object-top border border-[color:var(--gold)]/30"
                  data-testid="heritage-stepfather-photo"
                />
                <p className="eyebrow">Step-Father</p>
                <h3 className="font-serif text-3xl lg:text-4xl tracking-tighter font-light leading-tight">
                  Ted Cohen
                </h3>
                <p className="font-serif italic text-base text-white/60">Guitarist &middot; Fat Larry&rsquo;s Band</p>
                <p className="text-sm text-white/60 leading-relaxed">
                  Founding guitarist of Philadelphia R&amp;B / funk titans Fat
                  Larry&rsquo;s Band &mdash; the group behind global hit{" "}
                  <em className="italic">&ldquo;Zoom&rdquo;</em> (UK #2, 1982) and{" "}
                  <em className="italic">&ldquo;Act Like You Know&rdquo;</em>. Sampled by N.W.A,
                  Run-D.M.C., and Ice-T. Nina grew up in the next room while those
                  records were being written.
                </p>
                <div className="flex flex-wrap gap-3 pt-1">
                  <a
                    href="https://open.spotify.com/search/Fat%20Larry%27s%20Band"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em]"
                    style={{ color: "var(--gold)" }}
                  >
                    Spotify
                  </a>
                  <a
                    href="https://www.youtube.com/results?search_query=Fat+Larry%27s+Band+Zoom"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em]"
                    style={{ color: "var(--gold)" }}
                  >
                    YouTube
                  </a>
                  <a
                    href="https://en.wikipedia.org/wiki/Fat_Larry%27s_Band"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-white/55"
                  >
                    Wikipedia
                  </a>
                </div>
              </article>
            </div>
          </Section>

          {/* DEDICATION — LONI GAMBLE */}
          <section
            id="dedication"
            className="scroll-mt-28 py-14 border-t border-white/5"
            data-testid="about-dedication"
          >
            <div className="surface relative overflow-hidden p-8 sm:p-12 lg:p-16 text-center">
              <div
                className="absolute inset-0 pointer-events-none opacity-50"
                style={{
                  background:
                    "radial-gradient(ellipse 60% 80% at 50% 0%, rgba(212,175,55,0.18), transparent 70%), radial-gradient(ellipse 60% 80% at 50% 100%, rgba(74,14,78,0.4), transparent 70%)",
                }}
              />
              <div className="relative space-y-6">
                <div className="flex items-center justify-center gap-3">
                  <span className="h-px w-10" style={{ background: "rgba(232,180,184,0.6)" }} />
                  <p className="eyebrow">In Loving Memory &middot; 1955 &mdash; 2024</p>
                  <span className="h-px w-10" style={{ background: "rgba(232,180,184,0.6)" }} />
                </div>
                <h2 className="font-serif text-4xl sm:text-5xl lg:text-7xl tracking-tighter font-light leading-[1]">
                  Loni{" "}
                  <em className="italic font-light" style={{ color: "var(--gold)" }}>
                    Gamble
                  </em>
                </h2>
                <p className="font-serif italic text-base text-white/55">
                  Philadelphia &middot; &ldquo;Mickey&rdquo; to family
                </p>
                <p className="font-serif italic text-lg lg:text-xl text-white/70 max-w-2xl mx-auto leading-relaxed">
                  &ldquo;He saw the artist in me before the world did. Every stage I
                  stand on, every door I leave open, was built on the foundation
                  he laid.&rdquo;
                </p>
                <p className="text-xs uppercase tracking-[0.3em] text-white/45">&mdash; Nina Capone</p>

                <div className="h-px w-24 mx-auto" style={{ background: "rgba(232,180,184,0.4)" }} />

                <div className="pt-2" data-testid="loni-photo-strip">
                  <p className="eyebrow mb-4">Loni Gamble &amp; Sound Cheque</p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-3xl mx-auto">
                    {SOUND_CHEQUE_PHOTOS.map((p) => (
                      <div
                        key={p.src}
                        className="relative overflow-hidden rounded-sm border border-white/10 aspect-square group"
                      >
                        <img
                          src={imgUrl(p.src)}
                          alt={p.alt}
                          loading="lazy"
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                          style={{ filter: "sepia(0.15)" }}
                          data-testid="loni-photo"
                        />
                      </div>
                    ))}
                  </div>
                </div>

                <div className="grid md:grid-cols-3 gap-6 max-w-3xl mx-auto pt-2 text-left">
                  <div>
                    <p className="eyebrow mb-2">Career</p>
                    <p className="text-sm text-white/65 leading-relaxed">
                      Lead guitarist for the Grammy-nominated{" "}
                      <em className="italic">The Stylistics</em>; toured the world.
                      His solo debut{" "}
                      <em className="italic">&ldquo;Could It Be Love&rdquo;</em> won{" "}
                      <em className="italic">two 1984 Canadian Dance Music Awards</em>{" "}
                      (Best Record &middot; Top Male Vocalist) and charted Top 40
                      Dance in seven countries.
                    </p>
                  </div>
                  <div>
                    <p className="eyebrow mb-2">Sound Cheque</p>
                    <p className="text-sm text-white/65 leading-relaxed">
                      Founded{" "}
                      <em className="italic">Loni Gamble &amp; Sound Cheque / Sugar &amp; Spice</em>{" "}
                      with his late wife Beatrice &ldquo;Sugar&rdquo; &mdash; the same band that
                      featured Nina&rsquo;s mother on vocals as{" "}
                      <em className="italic">&ldquo;Spice.&rdquo;</em> Music ran through the
                      family because Loni built it that way.
                    </p>
                  </div>
                  <div>
                    <p className="eyebrow mb-2">Legacy</p>
                    <p className="text-sm text-white/65 leading-relaxed">
                      Founder of <em className="italic">CAPPA</em> (Community Alliance
                      for Positive Progressive Action), the{" "}
                      <em className="italic">Young Gentlemen Project</em>, and{" "}
                      <em className="italic">Phenomenal Young Ladies</em> &mdash; twenty
                      years of mentorship for at-risk youth in Williamsport, PA.
                    </p>
                  </div>
                </div>

                <p className="text-sm text-white/55 max-w-xl mx-auto leading-relaxed pt-2">
                  First manager. Mentor. Believer. Loni guided Nina from her 1992
                  debut tour as Lay-D-Bug through her earliest television moment in
                  1993. His vision lives in every record, every broadcast, every
                  young artist this empire reaches.
                </p>

                <div className="flex flex-wrap items-center justify-center gap-4 pt-2">
                  <a
                    href="https://www.youtube.com/results?search_query=Loni+Gamble+Could+It+Be+Love"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-ghost-gold inline-flex items-center gap-2 px-6 py-3 text-xs"
                    data-testid="about-loni-listen-link"
                  >
                    Listen &middot; &ldquo;Could It Be Love&rdquo;
                  </a>
                  <a
                    href="https://www.lycoming.edu/gamblescholarship"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs uppercase tracking-[0.25em]"
                    style={{ color: "var(--gold)" }}
                    data-testid="about-loni-scholarship-link"
                  >
                    Honor His Legacy &rarr;
                    <br />
                    <span className="text-[10px] tracking-[0.2em] text-white/55 normal-case">
                      Beatrice &ldquo;Momma B&rdquo; Gamble Scholarship &middot; Lycoming College
                    </span>
                  </a>
                </div>

                <p className="font-serif italic text-base pt-2" style={{ color: "var(--gold)" }}>
                  Forever in the credits. Forever in the prayer.
                </p>
              </div>
            </div>
          </section>

          {/* DISCOGRAPHY */}
          <Section id="discography" icon={Disc3} number="03" label="Discography" title="The catalog.">
            <div className="grid sm:grid-cols-2 gap-5 mb-10">
              {ALBUMS.map((a) => (
                <a
                  key={a.title}
                  href={a.spotify}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group surface p-4 flex gap-4 items-center hover:border-[color:var(--gold)]/40 transition-colors"
                  data-testid={`album-${a.title.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <img src={a.image} alt={a.title} className="w-24 h-24 object-cover rounded" loading="lazy" />
                  <div>
                    <p className="eyebrow" style={{ color: "var(--gold)" }}>Album · {a.year}</p>
                    <h3 className="font-serif text-2xl tracking-tight mt-0.5 group-hover:text-[color:var(--gold)] transition-colors">{a.title}</h3>
                    <p className="text-xs text-white/45 mt-1">{a.date} · {a.tracks} tracks</p>
                    <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] mt-2" style={{ color: "#1DB954" }}>
                      <ExternalLink size={12} /> Spotify
                    </span>
                  </div>
                </a>
              ))}
            </div>
            <p className="eyebrow mb-4">Singles</p>
            <div className="flex flex-wrap gap-2">
              {singles.map((s) => (
                <a
                  key={s.title}
                  href={s.apple}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full border border-white/10 text-xs text-white/70 hover:text-[color:var(--gold)] hover:border-[color:var(--gold)]/40 transition-all"
                  data-testid="single-item"
                >
                  {s.title} <span className="text-white/35">· {s.year}</span>
                </a>
              ))}
            </div>
          </Section>

          {/* FILMOGRAPHY */}
          <Section id="filmography" icon={Film} number="04" label="Filmography" title="On screen.">
            {FILMOGRAPHY.map((f) => (
              <article
                key={f.title}
                className="surface overflow-hidden max-w-2xl flex flex-col sm:flex-row"
                data-testid="film-item"
              >
                {f.poster && (
                  <img
                    src={imgUrl(f.poster)}
                    alt={`${f.title} (${f.year}) movie poster`}
                    loading="lazy"
                    className="w-full sm:w-40 h-56 sm:h-auto object-cover shrink-0 border-b sm:border-b-0 sm:border-r border-white/10"
                    data-testid="film-poster"
                  />
                )}
                <div className="p-6 flex flex-col">
                  <div className="flex items-center gap-3 flex-wrap">
                    <h3 className="font-serif text-2xl tracking-tight">{f.title} <span className="text-white/40 text-lg">({f.year})</span></h3>
                    {f.genre && (
                      <span className="text-[10px] uppercase tracking-[0.2em] px-2 py-1 rounded-full border border-[color:var(--gold)]/40 text-[color:var(--gold)]">{f.genre}</span>
                    )}
                  </div>
                  <p className="text-sm text-white/55 mt-1">as {f.role} · credited as {f.credited}</p>
                  {f.logline && (
                    <p className="text-white/70 text-sm mt-3 leading-relaxed italic">{f.logline}</p>
                  )}
                  <div className="flex flex-wrap items-center gap-3 mt-4 pt-1">
                    {f.watchUrl && (
                      <a
                        href={f.watchUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 rounded-full bg-[color:var(--gold)] text-black px-4 py-2 text-xs uppercase tracking-[0.18em] font-medium hover:opacity-90 transition-opacity"
                        data-testid="film-watch"
                      >
                        <Play size={14} fill="currentColor" /> {f.watchLabel || "Watch"}
                      </a>
                    )}
                    <a
                      href={f.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] text-white/60 hover:text-[color:var(--gold)] transition-colors"
                      data-testid="film-info"
                    >
                      More info <ExternalLink size={13} />
                    </a>
                  </div>
                </div>
              </article>
            ))}
            <p className="text-white/40 text-sm mt-5 italic">Future appearances to be announced.</p>
          </Section>

          {/* PODCASTS */}
          <Section id="podcasts" icon={Mic} number="05" label="Podcasts" title="On the mic.">
            <div className="grid sm:grid-cols-2 gap-5">
              {PODCASTS.map((p) => (
                <a
                  key={p.name}
                  href={p.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group surface p-6 hover:border-[color:var(--gold)]/40 transition-colors"
                  data-testid="podcast-item"
                >
                  <h3 className="font-serif text-2xl tracking-tight group-hover:text-[color:var(--gold)] transition-colors">{p.name}</h3>
                  <p className="eyebrow mt-1">{p.network}</p>
                  <p className="text-white/60 text-sm mt-3 leading-relaxed">{p.desc}</p>
                </a>
              ))}
            </div>
          </Section>

          {/* BUSINESSES */}
          <Section id="businesses" icon={Building2} number="06" label="Businesses" title="The ventures.">
            <div className="grid sm:grid-cols-2 gap-4">
              {BUSINESSES.map((b) => {
                const Wrap = b.internal ? Link : "a";
                const props = b.internal ? { to: b.url } : { href: b.url, target: "_blank", rel: "noopener noreferrer" };
                return (
                  <Wrap
                    key={b.name}
                    {...props}
                    className="group surface p-5 hover:border-[color:var(--gold)]/40 transition-colors block"
                    data-testid="business-item"
                  >
                    <div className="flex items-center justify-between">
                      <h3 className="font-serif text-xl tracking-tight group-hover:text-[color:var(--gold)] transition-colors">{b.name}</h3>
                      <ExternalLink size={14} className="text-white/30 group-hover:text-[color:var(--gold)]" />
                    </div>
                    <p className="text-white/55 text-sm mt-2 leading-relaxed">{b.desc}</p>
                    {b.cta && (
                      <span
                        data-testid="business-cta"
                        className="inline-flex items-center gap-1.5 mt-4 rounded-full border border-[color:var(--gold)]/40 px-4 py-1.5 text-[11px] uppercase tracking-[0.2em] text-[color:var(--gold)] group-hover:bg-[color:var(--gold)]/10 transition-colors"
                      >
                        {b.cta}
                        <ExternalLink size={12} />
                      </span>
                    )}
                  </Wrap>
                );
              })}
            </div>
          </Section>

          {/* AWARDS */}
          <Section id="awards" icon={Award} number="07" label="Awards & Recognition">
            {(() => {
              const iw = press.find((p) => (p.source || "").toLowerCase().includes("influential"));
              if (!iw) return <p className="text-white/45 italic">Recognition coming soon.</p>;
              const Wrap = iw.href ? "a" : "div";
              const wprops = iw.href
                ? { href: iw.href, target: "_blank", rel: "noopener noreferrer" }
                : {};
              return (
                <Wrap
                  {...wprops}
                  className="group surface overflow-hidden block max-w-xl hover:border-[color:var(--gold)]/40 transition-all"
                  data-testid="award-influential-women"
                >
                  {iw.image && (
                    <div className="h-64 overflow-hidden bg-black/40">
                      <img
                        src={imgUrl(iw.image)}
                        alt={iw.title || iw.source}
                        loading="lazy"
                        className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                      />
                    </div>
                  )}
                  <div className="p-6">
                    <p className="eyebrow" style={{ color: "var(--gold)" }}>{iw.source}</p>
                    <h3 className="font-serif text-2xl tracking-tight mt-1 leading-snug group-hover:text-[color:var(--gold)] transition-colors">
                      {iw.title}
                    </h3>
                    {iw.href && (
                      <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.22em] text-white/50 mt-3">
                        <ExternalLink size={12} /> Read the feature
                      </span>
                    )}
                  </div>
                </Wrap>
              );
            })()}
          </Section>

          {/* MEDIA */}
          <Section id="media" icon={Newspaper} number="08" label="Media" title="Press & appearances.">
            <div className="grid sm:grid-cols-2 gap-4 mb-8">
              {["Press articles", "Podcast interviews", "Television", "Radio"].map((cat) => (
                <div key={cat} className="surface p-5" data-testid="media-cat">
                  <p className="eyebrow" style={{ color: "var(--gold)" }}>{cat}</p>
                  <p className="text-white/45 text-sm mt-2">
                    {cat === "Press articles" && press.length > 0
                      ? `Featured coverage — see the Press page.`
                      : "Selected coverage — inquiries welcome via Booking below."}
                  </p>
                </div>
              ))}
            </div>
            <div className="surface p-6 max-w-2xl">
              <p className="eyebrow" style={{ color: "var(--gold)" }}>Media Kit</p>
              <p className="text-white/65 text-sm mt-2 leading-relaxed">
                High-res photos, bios, logos, and fact sheet available on request for press, promoters, and partners.
              </p>
              <a href="#booking" className="link-gold inline-flex items-center gap-2 text-sm mt-4">Request media kit →</a>
            </div>
            <p className="text-white/45 text-sm mt-6">
              More press &amp; vintage clippings on the{" "}
              <Link to="/press" className="link-gold" data-testid="media-press-link">Press page →</Link>
            </p>
          </Section>

          {/* GALLERY */}
          <Section id="gallery" icon={Camera} number="09" label="Gallery">
            <div className="flex flex-wrap gap-2 mb-6" data-testid="gallery-tabs">
              {galleryCats.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => { setGalleryCat(c); setLightboxIdx(null); }}
                  data-testid={`gallery-tab-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className={`text-[11px] uppercase tracking-[0.2em] px-4 py-1.5 rounded-full border transition-colors ${
                    galleryCat === c
                      ? "bg-[color:var(--gold)] text-black border-[color:var(--gold)]"
                      : "border-white/15 text-white/60 hover:border-[color:var(--gold)]/50 hover:text-white"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {galleryFiltered.map((g, i) => (
                <button
                  key={`${galleryCat}-${i}`}
                  type="button"
                  onClick={() => setLightboxIdx(i)}
                  className="group relative aspect-square overflow-hidden rounded surface block w-full focus:outline-none focus:ring-2 focus:ring-[color:var(--gold)]"
                  data-testid="gallery-item"
                  aria-label={`View ${g.alt}`}
                >
                  <img src={g.src} alt={g.alt} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                  <span className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-3">
                    <span className="text-white text-[11px] leading-snug tracking-wide">{g.alt}</span>
                  </span>
                </button>
              ))}
            </div>
            <p className="text-white/40 text-sm mt-4 italic">Professional photos, album covers, event & speaking imagery. Tap any photo to enlarge.</p>
          </Section>

          {/* GALLERY LIGHTBOX */}
          {lightboxPhoto && (
            <div
              className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8"
              onClick={() => setLightboxIdx(null)}
              onTouchStart={onTouchStart}
              onTouchEnd={onTouchEnd}
              data-testid="gallery-lightbox"
            >
              <button
                type="button"
                onClick={() => setLightboxIdx(null)}
                className="absolute top-5 right-5 text-white/70 hover:text-[color:var(--gold)] transition-colors z-10"
                aria-label="Close"
                data-testid="gallery-lightbox-close"
              >
                <X size={28} />
              </button>
              {galleryFiltered.length > 1 && (
                <>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); showPrev(); }}
                    className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-[color:var(--gold)] hover:text-black text-white flex items-center justify-center transition-colors z-10"
                    aria-label="Previous photo"
                    data-testid="gallery-lightbox-prev"
                  >
                    <ChevronLeft size={24} />
                  </button>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); showNext(); }}
                    className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-[color:var(--gold)] hover:text-black text-white flex items-center justify-center transition-colors z-10"
                    aria-label="Next photo"
                    data-testid="gallery-lightbox-next"
                  >
                    <ChevronRight size={24} />
                  </button>
                </>
              )}
              <figure className="max-w-5xl w-full flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
                <img
                  src={lightboxPhoto.src}
                  alt={lightboxPhoto.alt}
                  className="max-h-[80vh] w-auto max-w-full object-contain rounded shadow-2xl"
                  data-testid="gallery-lightbox-image"
                />
                <figcaption className="mt-4 text-center text-white/70 text-sm tracking-wide font-serif italic" data-testid="gallery-lightbox-caption">
                  {lightboxPhoto.alt}
                  <span className="block mt-1 text-white/35 text-xs not-italic tracking-[0.2em] uppercase">
                    {lightboxIdx + 1} / {galleryFiltered.length}
                  </span>
                </figcaption>
              </figure>
            </div>
          )}

          {/* FAQs */}
          <Section id="faqs" icon={HelpCircle} number="10" label="FAQs" title="Questions, answered.">
            <div className="space-y-10">
              {Object.entries(FAQS).map(([group, items]) => (
                <div key={group}>
                  <p className="eyebrow mb-4" style={{ color: "var(--gold)" }}>{group}</p>
                  <div className="space-y-3">
                    {items.map((item, i) => (
                      <details key={i} className="surface p-5 group" data-testid="faq-item">
                        <summary className="cursor-pointer font-serif text-lg tracking-tight list-none flex items-center justify-between">
                          {item.q}
                          <span className="text-[color:var(--gold)] group-open:rotate-45 transition-transform text-xl leading-none">+</span>
                        </summary>
                        <p className="text-white/65 text-sm mt-3 leading-relaxed">{item.a}</p>
                      </details>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </Section>

          {/* PROFILES + ENTITY IDS */}
          <Section id="profiles" icon={Fingerprint} number="11" label="Verified Profiles & Entity IDs" title="The knowledge graph.">
            <div className="flex flex-wrap gap-2.5 mb-10">
              {PROFILES.map((p) => (
                <a
                  key={p.label}
                  href={p.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-white/10 text-sm text-white/75 hover:text-[color:var(--gold)] hover:border-[color:var(--gold)]/40 transition-all"
                  data-testid="profile-link"
                >
                  <BadgeCheck size={14} style={{ color: "var(--gold)" }} /> {p.label}
                </a>
              ))}
            </div>
            <div className="surface p-6">
              <p className="eyebrow mb-4" style={{ color: "var(--gold)" }}>Published Entity IDs</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
                {ENTITY_IDS.map((e) => (
                  <a
                    key={e.label}
                    href={e.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-baseline justify-between gap-4 py-2 border-b border-white/5 group min-w-0"
                    data-testid="entity-id"
                  >
                    <span className="text-xs uppercase tracking-[0.2em] text-white/50 shrink-0">{e.label}</span>
                    <span className="font-mono text-xs text-white/80 group-hover:text-[color:var(--gold)] transition-colors truncate">{e.value}</span>
                  </a>
                ))}
              </div>
            </div>
          </Section>

          {/* AUTHORITY & VERIFIED SOURCES */}
          <Section id="authority" icon={BadgeCheck} number="12" label="Authority & Verified Sources" title="Independently verified.">
            <p className="text-white/60 max-w-2xl mb-10 leading-relaxed">
              The identity, catalog, businesses and press behind Nina Capone — corroborated by independent, authoritative sources across music, film, media and industry.
            </p>
            <div className="space-y-10">
              {AUTHORITY_SOURCES.map((grp) => (
                <div key={grp.group} data-testid="authority-group">
                  <p className="eyebrow mb-4" style={{ color: "var(--gold)" }}>{grp.group}</p>
                  <div className="grid md:grid-cols-2 gap-4">
                    {grp.items.map((it) => (
                      <a
                        key={it.name}
                        href={it.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="surface p-5 flex flex-col gap-2 group hover:border-[color:var(--gold)]/40 transition-all"
                        data-testid="authority-source"
                      >
                        <div className="flex items-center justify-between gap-3">
                          <span className="text-white font-medium group-hover:text-[color:var(--gold)] transition-colors inline-flex items-center gap-2">
                            <BadgeCheck size={15} style={{ color: "var(--gold)" }} /> {it.name}
                          </span>
                          <span
                            className="text-[9px] uppercase tracking-[0.18em] px-2 py-1 rounded-full border border-white/10 text-white/50 shrink-0"
                            data-testid="authority-priority"
                          >
                            {it.priority}
                          </span>
                        </div>
                        <p className="text-sm text-white/55 leading-relaxed">{it.note}</p>
                        <span className="text-[11px] text-white/35 inline-flex items-center gap-1 mt-1">
                          <ExternalLink size={11} /> {new URL(it.url).hostname.replace("www.", "")}
                        </span>
                      </a>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </Section>

          {/* BOOKING */}
          <Section id="booking" icon={CalendarDays} number="13" label="Booking" title="Work with Nina.">
            <p className="text-white/60 max-w-2xl mb-8 leading-relaxed">
              Performances, keynote speaking, panels, and AI-tech / business consulting via My Biz Consulting. Send the details and Nina's team will follow up.
            </p>
            <form onSubmit={submitBooking} className="grid sm:grid-cols-2 gap-4 max-w-3xl" data-testid="about-booking-form">
              <input className={input} placeholder="Your name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="about-booking-name" />
              <input className={input} type="email" placeholder="Email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="about-booking-email" />
              <input className={input} placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="about-booking-phone" />
              <input className={input} placeholder="Organization" value={form.organization} onChange={(e) => setForm({ ...form, organization: e.target.value })} data-testid="about-booking-org" />
              <select className={input} value={form.booking_type} onChange={(e) => setForm({ ...form, booking_type: e.target.value })} data-testid="about-booking-type">
                <option value="music">Performance / Music</option>
                <option value="speaking">Speaking / Keynote</option>
                <option value="consulting">Consulting</option>
              </select>
              <input className={input} type="date" value={form.event_date} onChange={(e) => setForm({ ...form, event_date: e.target.value })} data-testid="about-booking-date" />
              <input className={input} placeholder="Location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="about-booking-location" />
              <input className={input} placeholder="Budget (optional)" value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })} data-testid="about-booking-budget" />
              <textarea className={`${input} sm:col-span-2`} rows={4} placeholder="Tell us about the opportunity" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="about-booking-message" />
              <button
                type="submit"
                disabled={submitting}
                className="btn-gold sm:col-span-2 py-3.5 text-xs disabled:opacity-60"
                data-testid="about-booking-submit"
              >
                {submitting ? "Sending…" : "Send Booking Request"}
              </button>
            </form>
          </Section>
        </div>
      </div>
    </div>
  );
}
